import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Sparkles, Pin } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORTED_SPECIES = ['Seabream', 'Seabass', 'Tilapia'];

/**
 * UpdateCuratedExampleButton
 *
 * Admin-only CTA with two modes:
 *
 *   1. UPDATE mode — the currently-loaded record IS already the curated
 *      Try-[Species] example. Saves form edits back into that source record
 *      (via `/inline-update`) so every user clicking Try-X sees the new
 *      values immediately.
 *
 *   2. PIN mode — the currently-loaded record is NOT the curated example.
 *      One click repins the curated Try-[Species] entry for this module at
 *      the current record (via `PUT /api/admin/curated-examples`). The admin
 *      no longer has to leave the page and open the Curated Examples panel
 *      just to promote a report they're already viewing.
 *
 * Visibility rules:
 *   • `isAdmin` must be true (button hidden for non-admins).
 *   • A saved `currentRecordId` must be loaded.
 *   • `currentSpecies` (or `species`) must be one of the supported species.
 *
 * Props:
 *   module            one of: snapshot | sfr | growth_model | growth_prognosis | spreadsheet
 *   species           current species used for pinning. Falls back to the
 *                     curated example's species when in UPDATE mode.
 *   currentSpecies    alias for `species` — use this when you want PIN mode
 *                     to work regardless of whether a curated example is loaded.
 *   token             auth bearer.
 *   isAdmin           boolean.
 *   sourceRecordId    id from `curated-examples`.source_record_id (nullable).
 *   currentRecordId   id of the record loaded in the page right now.
 *   buildRecordPatch  () => Object — called in UPDATE mode. The page returns
 *                     the $set map applied to the source record.
 *   currentName       current display name (prefilled in the rename input in
 *                     UPDATE mode).
 *   onUpdated         () => void — called after a successful save to refetch
 *                     curated examples.
 */
export default function UpdateCuratedExampleButton({
  module,
  species,
  currentSpecies,
  token,
  isAdmin,
  sourceRecordId,
  currentRecordId,
  buildRecordPatch,
  currentName = '',
  onUpdated,
}) {
  const [open, setOpen] = useState(false);
  const [renameTo, setRenameTo] = useState(currentName || '');
  const [saving, setSaving] = useState(false);

  if (!isAdmin) return null;
  if (!currentRecordId) return null;

  // Prefer the caller-supplied currentSpecies so PIN mode works on any
  // record — fall back to the curated-example species when we're in
  // UPDATE mode (same record as the pinned one).
  const effectiveSpecies = currentSpecies || species;
  if (!effectiveSpecies || !SUPPORTED_SPECIES.includes(effectiveSpecies)) return null;

  const isUpdateMode = !!sourceRecordId && String(sourceRecordId) === String(currentRecordId);

  const submit = async () => {
    setSaving(true);
    try {
      if (isUpdateMode) {
        const patch = (typeof buildRecordPatch === 'function' ? buildRecordPatch() : {}) || {};
        const body = {
          record_patch: patch,
          rename_to: renameTo && renameTo.trim() !== currentName ? renameTo.trim() : null,
        };
        await axios.put(
          `${API}/admin/curated-examples/${module}/${effectiveSpecies}/inline-update`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Try-${effectiveSpecies} example updated for all users.`);
      } else {
        // PIN mode: promote this record to be the Try-[Species] example.
        await axios.put(
          `${API}/admin/curated-examples`,
          {
            module,
            species: effectiveSpecies,
            source_record_id: currentRecordId,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Pinned as the Try-${effectiveSpecies} example for all users.`);
      }
      setOpen(false);
      onUpdated?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : `Failed to ${isUpdateMode ? 'update' : 'pin'} curated example`;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const triggerLabel = isUpdateMode
    ? `Update Try-${effectiveSpecies} Example`
    : `Pin as Try-${effectiveSpecies} Example`;

  const dialogTitle = isUpdateMode
    ? `Update Try-${effectiveSpecies} example`
    : `Pin as Try-${effectiveSpecies} example`;

  const dialogBody = isUpdateMode ? (
    <>
      The current form values will overwrite the curated{' '}
      <span className="font-semibold">{effectiveSpecies}</span> example for the{' '}
      <span className="font-semibold">{moduleLabel(module)}</span> module.
      Every user clicking <span className="italic">Try {effectiveSpecies}</span> after
      this will see the new values.
    </>
  ) : (
    <>
      This will replace the current Try-<span className="font-semibold">{effectiveSpecies}</span>{' '}
      example for the <span className="font-semibold">{moduleLabel(module)}</span> module with
      the record loaded on this page. Every user clicking{' '}
      <span className="italic">Try {effectiveSpecies}</span> afterward will see this
      report's inputs.
    </>
  );

  const testidSlug = `${module}-${effectiveSpecies.toLowerCase()}`;
  const Icon = isUpdateMode ? Sparkles : Pin;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          setRenameTo(currentName || '');
          setOpen(true);
        }}
        className={
          isUpdateMode
            ? 'border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800'
            : 'border-cyan-300 bg-cyan-50 hover:bg-cyan-100 text-cyan-800'
        }
        data-testid={`${isUpdateMode ? 'update' : 'pin'}-curated-${testidSlug}-btn`}
        title={
          isUpdateMode
            ? `Save these changes back to the Try-${effectiveSpecies} example (admin)`
            : `Promote this report to be the Try-${effectiveSpecies} example (admin)`
        }
      >
        <Icon className="h-3.5 w-3.5 mr-1.5" />
        {triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-md"
          data-testid={`${isUpdateMode ? 'update' : 'pin'}-curated-${testidSlug}-dialog`}
        >
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isUpdateMode ? 'text-amber-900' : 'text-cyan-900'}`}>
              <Icon className="h-4 w-4" />
              {dialogTitle}
            </DialogTitle>
            <DialogDescription className="leading-relaxed">
              {dialogBody}
            </DialogDescription>
          </DialogHeader>

          {isUpdateMode && (
            <div className="space-y-2 py-2">
              <Label htmlFor="curated-rename" className="text-sm">
                Rename example (optional)
              </Label>
              <Input
                id="curated-rename"
                value={renameTo}
                onChange={(e) => setRenameTo(e.target.value)}
                placeholder={currentName || `Try-${effectiveSpecies} example`}
                data-testid={`update-curated-${testidSlug}-rename`}
              />
              <p className="text-xs text-slate-500">
                Visible in the Admin &gt; Curated Examples panel and as the source-record label.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={saving}
              className={
                isUpdateMode
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'bg-cyan-600 hover:bg-cyan-700 text-white'
              }
              data-testid={`${isUpdateMode ? 'update' : 'pin'}-curated-${testidSlug}-confirm`}
            >
              {saving ? (
                isUpdateMode ? 'Saving…' : 'Pinning…'
              ) : (
                <>
                  {isUpdateMode ? (
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                  ) : (
                    <Pin className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {isUpdateMode ? 'Save for all users' : `Pin for all users`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function moduleLabel(module) {
  return (
    {
      snapshot: 'Snapshot Analysis',
      sfr: 'SFR Calculator',
      growth_model: 'Growth vs Model',
      growth_prognosis: 'Growth Prognosis',
      spreadsheet: 'Spreadsheet Comparison',
    }[module] || module
  );
}
